import type { UserDto } from "@reactive-resume/dto";
import { useQuery } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import { useEffect } from "react";

import { isDefinitiveAuthFailure } from "@/client/libs/auth-failure";
import { axios } from "@/client/libs/axios";
import { useAuthStore } from "@/client/stores/auth";

export const fetchUser = async () => {
  const response = await axios.get<UserDto | undefined, AxiosResponse<UserDto | undefined>>(
    "/user/me",
  );

  return response.data ?? null;
};

const AUTH_STORE_KEY = "auth";

/**
 * Cheap heuristic for "could the visitor plausibly have a session?"
 * The server's `Authentication` and `Refresh` cookies are HttpOnly so JS
 * can't read them directly. Instead we rely on Zustand's persist
 * middleware: if `auth` localStorage has ever been written with a non-null
 * user, treat that as "previously authenticated" and run the /me check to
 * refresh. If localStorage is empty / contains a null user, this is an
 * anonymous visitor — skip the /me + /refresh round-trip entirely.
 *
 * Net effect (perf audit item #8): public marketing pages load with zero
 * auth-related network requests for first-time visitors. Logged-in
 * visitors are unchanged — Zustand still has their user, fetch still
 * fires to refresh.
 */
const hasAuthHint = (): boolean => {
  if (typeof window === "undefined") return false;

  try {
    const raw = window.localStorage.getItem(AUTH_STORE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { state?: { user?: unknown } };
    return Boolean(parsed.state?.user);
  } catch {
    return false;
  }
};

/**
 * `useUser({ force: true })` from inside AuthGuard / authed routes always
 * fires the /me probe — the HttpOnly cookie is authoritative, the
 * localStorage hint is only an optimization. Default behaviour (no opts)
 * keeps the hint short-circuit so public marketing pages skip /me + /refresh
 * for first-time visitors (perf audit item #8).
 *
 * `refetchInterval` polls /me on a timer. Added for the post-checkout return
 * page, which must WAIT for the billing webhook to grant the tier rather than
 * assume it did — the subscription flips asynchronously, so the page has to
 * watch for it instead of guessing.
 *
 * `skip: true` unconditionally disables the query, overriding `force` and the
 * localStorage hint. Added for a bare `/billing/return` visit (no app-issued
 * `success` marker): there is nothing to confirm, so the page must make ZERO
 * `/user/me` calls rather than poll on the strength of an unrelated logged-in
 * session.
 */
export const useUser = (options?: {
  force?: boolean;
  refetchInterval?: number;
  skip?: boolean;
}) => {
  const setUser = useAuthStore((state) => state.setUser);
  const inMemoryUser = useAuthStore((state) => state.user);
  const force = options?.force === true;
  const skip = options?.skip === true;
  const enabled = !skip && (force || Boolean(inMemoryUser) || hasAuthHint());

  const {
    error,
    isError,
    isPending: loading,
    data: user,
    refetch,
  } = useQuery({
    queryKey: ["user"],
    queryFn: fetchUser,
    refetchOnMount: "always",
    refetchInterval: options?.refetchInterval,
    enabled,
    // Codex review on PR #577 (DO NOT MERGE #1): this hook does NOT own
    // long-lived retrying — AuthGuard's own reconnect poll does (a growing
    // backoff up to 30s, indefinitely, only while a protected route is
    // mounted). Retrying HERE too meant every failed refetch re-entered
    // axios.ts's OWN `/auth/refresh` retry loop, so a single outage fanned
    // out into (guard retries) × (hook retries) × (refresh retries) network
    // calls — an unbounded storm across every open tab. One shot; the
    // persist-effect guard below (not a query-level retry) is what stops a
    // transient failure from nulling the store.
    retry: false,
  });

  useEffect(() => {
    if (!enabled) return;
    // Don't touch the store before the FIRST fetch has settled one way or
    // the other. Without this, `user` is `undefined` on the very first
    // render (the query hasn't resolved yet), so `setUser(user ?? null)`
    // would null out an already-seeded store user before the /me check
    // even comes back — indistinguishable from a real logout for that one
    // tick, and exactly the write a transient failure must never cause.
    if (loading) return;
    // Do NOT persist null into the auth store on a transient failure — this
    // is what silently logged the founder out during every brief deploy
    // stall despite a live session. Only a definitive 401/403 (or a clean
    // success) may update the store.
    if (isError && !isDefinitiveAuthFailure(error)) return;
    setUser(user ?? null);
  }, [enabled, loading, user, setUser, isError, error]);

  return { user: enabled ? user : null, loading: enabled && loading, error, isError, refetch };
};
